import React, { useState, useEffect } from "react";
import DepartmentBanner from "../common/DepartmentBanner";
import DegreeAndCertificateService from "../../services/Department/DegreeAndCertificateService";
import DepartmentService from "../../services/Department/DepartmentService";
import styles from './DegreeAndCertificate.module.css';
import { FaSearch, FaSpinner, FaTable, FaTh, FaSort, FaSortUp, FaSortDown, FaDownload, FaGraduationCap, FaCertificate, FaCalendarAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Pagination from '../common/Pagination';

const DegreeAndCertificate = () => {
  document.title = "Degrees and Certificates";
  const params = new URLSearchParams(window.location.search);

  // Get base URL for downloads based on current origin
  const getDownloadBaseUrl = () => {
    if (window.location.origin.includes('localhost')) {
      return 'http://localhost';
    }
    return window.location.origin;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [disabledColumns, setDisabledColumns] = useState([]);
  const itemsPerPage = 20;

  // Define column types
  const COLUMN_TYPES = Object.freeze({
    TITLE: 28,
    ACADEMIC_YEAR: 29,
    FILE: 30
  });

  const SORT_KEYS = Object.freeze({
    TITLE: 'title',
    ACADEMIC_YEAR: 'academicYear',
    LOCATION: 'location'
  });

  // Check if a column is enabled
  const isColumnEnabled = (columnType) => {
    return !disabledColumns.includes(columnType);
  };

  useEffect(() => {

      // Get column configuration
      DepartmentService.GetColumnConfig(1)
        .then(response => {
          const { serviceDisabledItems } = response.data || response;
          if (serviceDisabledItems && serviceDisabledItems.trim() !== "") {
            setDisabledColumns(serviceDisabledItems.split(',').map(item => parseInt(item.trim())));
          } else {
            setDisabledColumns([]);
          }
        })
        .catch(error => {
          console.error('Error fetching column configuration:', error);
          setDisabledColumns([]);
        });

      fetchPrograms();
    
  }, []);

  const fetchPrograms = () => {
    setIsLoading(true);
    DegreeAndCertificateService.GetByDepartmentId()
      .then(response => {
        setPrograms(Array.isArray(response.data) ? response.data : []);
      })
      .catch(error => {
        console.error('Error fetching programs:', error);
        setPrograms([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filteredPrograms = programs.filter(program =>
    program.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    program.academicYear.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (program.location && program.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPrograms = (programs) => {
    if (!sortConfig.key) return programs;

    return [...programs].sort((a, b) => {
      const aValue = (a[sortConfig.key] || '').toLowerCase();
      const bValue = (b[sortConfig.key] || '').toLowerCase();
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" />
      : <FaSortDown className="ms-1" />;
  };

  const sortedPrograms = getSortedPrograms(filteredPrograms);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedPrograms.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container-fluid p-0" 
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
     {/* <DepartmentBanner /> */}
        
        <div className="container-custom py-4">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb bg-transparent p-0">
              <li className="breadcrumb-item">

              </li>
              {/* <li className="breadcrumb-item active" aria-current="page">Degrees & Certificates</li> */}
            </ol>
          </nav>

          {/* Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Programs</p>
                  <p className="mb-0">{programs.length}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Academic Years</p>
                  <p className="mb-0">
                    {new Set(programs.map(program => program.academicYear)).size}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Courses</p>
                  <p className="mb-0">
                    {programs.reduce((sum, program) => sum + program.totalCourses, 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-3">
                  <p className="mb-0 h3">Programs</p>
                  <p className="text-muted mb-0">Browse degrees and certificates</p>
                </div>
                <div className="col-lg-9">
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="flex-grow-1">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FaSearch className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search programs..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="btn-group view-toggle">
                      <button type="button" aria-label="Open Table"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                      >
                        <FaTable />
                      </button>
                      <button type="button" aria-label="Open Grid"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <div className="text-center py-5">
                <p className="text-muted">Loading degrees and certificates...</p>
              </div>
            ) : viewMode === 'table' ? (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="card shadow-sm border-0"
              >
                <div className={styles.tableWrapper}>
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        {isColumnEnabled(COLUMN_TYPES.TITLE) && (
                          <th 
                            className={`${styles.tableHeader} ${styles.titleCell} ${styles.cursorPointer}`}
                            onClick={() => requestSort(SORT_KEYS.TITLE)}
                          >
                            Title {getSortIcon(SORT_KEYS.TITLE)}
                          </th>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.ACADEMIC_YEAR) && (
                          <th 
                            className={`${styles.tableHeader} ${styles.yearCell} ${styles.cursorPointer}`}
                            onClick={() => requestSort(SORT_KEYS.ACADEMIC_YEAR)}
                          >
                            Academic Year {getSortIcon(SORT_KEYS.ACADEMIC_YEAR)}
                          </th>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.FILE) && (
                          <th className={`${styles.tableHeader} ${styles.fileCell}`}>
                            File
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((program, index) => (
                          <motion.tr 
                            key={index}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: index * 0.05 }}
                            className="align-middle"
                          >
                            {isColumnEnabled(COLUMN_TYPES.TITLE) && (
                              <td className={`${styles.tableCell} ${styles.titleCell}`}>
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-3">
                                    <FaGraduationCap className="text-primary" />
                                  </div>
                                  <div>
                                    <p className="mb-0 h6">{program.title}</p>
                                    <small className="text-muted">
                                      {program.totalCourses} {program.totalCourses === 1 ? 'Course' : 'Courses'}
                                    </small>
                                  </div>
                                </div>
                              </td>
                            )}
                            {isColumnEnabled(COLUMN_TYPES.ACADEMIC_YEAR) && (
                              <td className={`${styles.tableCell} ${styles.yearCell}`}>
                                <span className="badge bg-info">
                                  <FaCalendarAlt className="me-1" />
                                  {program.academicYear}
                                </span>
                              </td>
                            )}
                            {isColumnEnabled(COLUMN_TYPES.FILE) && (
                              <td className={`${styles.tableCell} ${styles.fileCell}`}>
                                {program.location ? (
                                  <a 
                                    href={`${getDownloadBaseUrl()}/shareddirectory/degree/${program.location}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <FaDownload className="me-1" />
                                    Download
                                  </a>
                                ) : (
                                  <span className="text-muted">No file</span>
                                )}
                              </td>
                            )}
                          </motion.tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={Object.values(COLUMN_TYPES).filter(type => isColumnEnabled(type)).length} className="text-center py-5">
                            <p className="text-muted mb-0">No programs found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination for table view */}
                <Pagination
                  currentPage={currentPage}
                  totalItems={sortedPrograms.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="row g-4"
              >
                {currentItems.map((program, index) => (
                  <motion.div
                    key={index}
                    className="col-md-6 col-lg-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="card h-100 border-0 shadow-sm hover-shadow">
                      <div className="card-body">
                        {isColumnEnabled(COLUMN_TYPES.TITLE) && (
                          <div className="d-flex align-items-center mb-3">
                            <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                              <FaGraduationCap className="text-primary" size={24} />
                            </div>
                            <div>
                              <h5 className="card-title mb-1">{program.title}</h5>
                              {isColumnEnabled(COLUMN_TYPES.ACADEMIC_YEAR) && (
                                <span className="badge bg-info">
                                  <FaCalendarAlt className="me-1" />
                                  {program.academicYear}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.TITLE) && (
                          <div className="mb-3">
                            <div className="d-flex align-items-center mb-2">
                              <FaCertificate className="text-primary me-2" />
                              <strong>Total Courses:</strong>
                              <span className="ms-2">{program.totalCourses}</span>
                            </div>
                          </div>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.FILE) && (
                          program.location ? (
                            <a 
                              href={`${getDownloadBaseUrl()}/shareddirectory/degree/${program.location}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-primary w-100"
                            >
                              <FaDownload className="me-2" />
                              Download File
                            </a>
                          ) : (
                            <button className="btn btn-outline-secondary w-100" disabled>
                              No File Available
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {currentItems.length === 0 && (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted mb-0">No programs found</p>
                  </div>
                )}
                {/* Pagination for grid view */}
                <div className="col-12 mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalItems={sortedPrograms.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default DegreeAndCertificate; 